import {
  Component,
  ViewChild,
  HostListener,
  OnInit,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { finalize, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs/Subject';
import { JoyrideService } from 'ngx-joyride';

import { SetTabHeaderBackgroundColour } from '../CustomUIOps';

import { UserStore } from 'app/@core/stores/user.store';
import { User } from 'app/@core/interfaces/common/users';
import {
  Patient,
  PatientData,
} from 'app/@core/interfaces/common/CalorieFriend/patients';
import { TutorialIDs } from 'app/@core/interfaces/common/CalorieFriend/tutorial';
import { PatientStore } from 'app/@core/stores/patient.store';
import { IComponentCanDeactivate } from 'app/@auth/guards/changes.guard';

import { GoalsListViewComponent } from './GoalsListView/goals-list-view.component';
import { PatientGeneralInfoComponent } from './patient-general-info/patient-general-info.component';
import { Observable } from 'rxjs';
import { ProgramOverviewComponent } from './program-overview/program-overview.component';
import { NbToastrService } from '@nebular/theme';
import { FitnessPlanData, MealPlanData, ProgramTemplateData } from 'app/@core/interfaces/common/CalorieFriend/plan';

@Component({
  selector: 'app-patient-sheet-view',
  templateUrl: './patient-sheet-view.component.html',
  styleUrls: [
    './patient-sheet-view.component.scss',
    '../global.component.scss',
  ],
})
export class PatientSheetViewComponent implements OnInit, AfterViewInit {

  @ViewChild('tabGroup') tabGroup?;
  @ViewChild('generalInfo') generalInfoView: PatientGeneralInfoComponent = null;
  @ViewChild('goals') goalsView: GoalsListViewComponent;
  @ViewChild('programOverviewComponent') programOverviewComponent: ProgramOverviewComponent;

  patient: Patient;
  patientId: number;
  patientFullName: string;

  private readonly unsubscribe$: Subject<void> = new Subject<void>();
  public isTutorial: boolean = false;
  public patientSubscriptionItem: any;
  public loading: boolean = false;
  private planType: string;
  public currentIndex: number = 0;

  constructor(
    public joyrideService: JoyrideService,
    private route: ActivatedRoute,
    private router: Router,
    private userStore: UserStore,
    private patientsService: PatientData,
    private fitnessPlansService: FitnessPlanData,
    private toastrService: NbToastrService,
    private patientStore: PatientStore,
  ) {}

  ngOnInit(): void {
    this.patientId = +this.route.snapshot.paramMap.get('PatientId');
    this.isTutorial = this.route.snapshot.url[2]?.toString().toUpperCase() === 'FROMTUTORIAL';
    this.planType = this.route.snapshot.queryParamMap.get('planType');

    this.loadPatientData();

  }

  ngAfterViewInit(): void {
    if (this.isTutorial) {
      this.tabGroup.selectedIndex = 1;
    }

    // set the background color of the tab group
    SetTabHeaderBackgroundColour(this.tabGroup.nativeElement);
    if (this.planType?.toUpperCase() === 'WORKOUT')
      this.tabGroup.selectedIndex = 1;
    else if (this.planType?.toUpperCase() === 'MEALPLAN')
      this.tabGroup.selectedIndex = 2;
    else if (this.planType?.toUpperCase() === 'MICROGOALS')
      this.tabGroup.selectedIndex = 3;
    else if (this.planType?.toUpperCase() === 'GOALS')
      this.tabGroup.selectedIndex = 4;
    else if (this.planType?.toUpperCase() === 'PROGRESS')
      this.tabGroup.selectedIndex = 5;
    else if (this.planType?.toUpperCase() === 'GENERALINFO')
      this.tabGroup.selectedIndex = 6;
    else if (this.planType?.toUpperCase() === 'PROGRAM')
      this.tabGroup.selectedIndex = 0;

    this.userStore
    .onUserStateChange()
    .pipe(takeUntil(this.unsubscribe$))
    .subscribe((u => this.startTutorial(u, this.isTutorial)));
  }


  ngOnDestroy(): void {
    this.patientStore.resetPatient();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private loadPatientData(): void {
    if (!this.patientId) return;

    this.patientsService
      .get(this.patientId, null)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((patient) => {
        this.patient = patient;
        this.patientStore.setPatient(patient);
        this.patientFullName = patient.name;
      });
      this.patientsService.getPatientSubscriptionItems(this.patientId).subscribe((items) => {
        this.patientSubscriptionItem = items;
      });
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any) {
    if (!this.canDeactivate()) {
      $event.returnValue =
        'This message is displayed to the user in IE and Edge when they navigate without using Angular routing (type another URL/close the browser/etc)';
    }
  }

  canDeactivate(): boolean {
    return true;
  }


  private startTutorial(user: User, isFromTutorial:boolean): void {
    if (!user) return;

    const tutorialStates = JSON.parse(user.tutorialStates ?? '{}');

    // check the tutorial step 1 - demo patient
    if (!tutorialStates[TutorialIDs.AddNewPlan] && isFromTutorial) {
      setTimeout(() => {
        this.joyrideService
          .startTour({
            steps: ['', '', '', TutorialIDs.AddNewPlan, '', '', ''],
            showPrevButton: false,
            showCounter: true,
          })
          .subscribe();
      }, 1000);
    }
  }

  onSend() {
    this.loading = true;
    this.fitnessPlansService
    .submitPatientNotification(this.patient.id, -1, true)
    .pipe(
      finalize(() => this.loading = false)
    )
    .subscribe(() =>  {

      let message  = "App Invite sent via Email."
      if (this.patient.phoneNumber.length > 0) {
        message  = "App Invite sent via Email and SMS."
      }
      this.toastrService.success('', message);
    })
    .add(() => {

    });
  }

  onChangeTab($event: MatTabChangeEvent): void {
    this.currentIndex = $event.index;
    let planTypeValue = '';

    switch (this.currentIndex) {
      case 1:
        planTypeValue = 'workout';
        break;
      case 2:
        planTypeValue = 'mealplan';
        break;
      case 3:
        planTypeValue = 'microgoals';
        break;
      case 4:
        planTypeValue = 'goals';
        break;
      case 5:
        planTypeValue = 'progress';
        break;
      case 6:
        planTypeValue = 'generalinfo';
        break;
      case 0:
      default:
        planTypeValue = 'program';
        break;
    }

    // Update the URL with the new planTypeValue as a query parameter
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { planType: planTypeValue },
      queryParamsHandling: 'merge', // Merge with existing query params
      replaceUrl: true // Replace the current URL without adding to the history stack
    });
  }
}
